/**
 * Paper headers.
 *
 * A template holds *structure* - which fields print, their labels, the logo, the
 * margins. The values live on each worksheet, so changing the school logo updates
 * every paper ever made without any paper being edited (DR-005).
 *
 * The live A4 preview beside the editor is driven by the same template draft that
 * is saved, so labels, fields, fixed lines, margins and footer settings update as
 * the administrator changes them.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, saveBlob } from '../../lib/api/client'
import type { Preview as PreviewData, Template } from '../../lib/api/client'
import {
  Button,
  Checkbox,
  ConfirmButton,
  Field,
  Input,
  Pill,
  Select,
  Spinner,
  Textarea,
} from '../../components/ui'
import { Icon } from '../../components/icons'
import { toast } from '../../components/toast'
import { sheetTypeLabel } from '../../lib/sheetTypes'
import { Preview as PaperPreview } from '../preview/Preview'

/** Mirrors HEADER_FIELD_LABELS in app/models/enums.py. The API returns configured
 *  fields, but the *available* ones are a fixed vocabulary. */
const FIELDS: [string, string][] = [
  ['exam_name', 'Exam'],
  ['class', 'Class'],
  ['section', 'Section'],
  ['subject', 'Subject'],
  ['date', 'Date'],
  ['duration', 'Time'],
  ['student_name', 'Name'],
  ['roll_number', 'Roll No.'],
  ['max_marks', 'Max. Marks'],
]

const REQUIRED_FIELDS = new Set(['class', 'student_name'])

const LAYOUTS: [string, string][] = [
  ['centered', 'Centred'],
  ['logo_left', 'Logo on the left'],
  ['logo_right', 'Logo on the right'],
  ['banner', 'Banner'],
]
const PAPER_LAYOUTS: [Template['paper_layout'], string][] = [
  ['standard', 'Standard paper'],
  ['exam_grid', 'Exam grid'],
]
const PAPER_FONTS = [
  'Palatino Linotype',
  'Arial',
  'Calibri',
  'Century Schoolbook',
  'Times New Roman',
] as const

export function Templates({ schoolId }: { schoolId?: string }) {
  const queryClient = useQueryClient()
  const [openId, setOpenId] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [preset, setPreset] = useState('Worksheet')

  const templatesKey = ['templates', schoolId ?? 'current']
  const templates = useQuery({
    queryKey: templatesKey,
    queryFn: () => api.templates(schoolId),
  })
  const presets = useQuery({ queryKey: ['presets'], queryFn: api.templatePresets })

  const refresh = () => queryClient.invalidateQueries({ queryKey: templatesKey })
  const failed = (error: Error) => toast(error.message, 'error')

  const create = useMutation({
    mutationFn: () => api.createTemplate({ name: newName, preset }, schoolId),
    onSuccess: (created) => {
      setNewName('')
      setOpenId(created.id)
      refresh()
    },
    onError: failed,
  })

  if (templates.isLoading) return <Spinner label="Loading paper headers" />

  return (
    <div className="tc-panel tc-template-panel">
      <div className="tc-panel-head tc-template-panel-head">
        <div>
          <h2>Paper headers</h2>
          <p>
            Set the school-wide header for each paper type. Every matching paper uses it.
          </p>
        </div>
        <div className="tc-template-create" aria-label="Create a paper header">
          <Input
            aria-label="New header name"
            placeholder="Header name"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
          />
          <Select
            aria-label="Start from template"
            value={preset}
            onChange={(event) => setPreset(event.target.value)}
          >
            {(presets.data ?? []).map((name) => (
              <option key={name} value={name}>
                Start from {name}
              </option>
            ))}
          </Select>
          <Button
            variant="primary"
            icon={<Icon.plus size={15} />}
            disabled={!newName.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Create header
          </Button>
        </div>
      </div>

      <div className="tc-template-list">
        {(templates.data ?? []).map((template) => (
          <TemplateRow
            key={template.id}
            template={template}
            open={openId === template.id}
            onToggle={() => setOpenId(openId === template.id ? null : template.id)}
            onChanged={refresh}
            schoolId={schoolId}
          />
        ))}
      </div>
    </div>
  )
}

function TemplateRow({
  template,
  open,
  onToggle,
  onChanged,
  schoolId,
}: {
  template: Template
  open: boolean
  onToggle: () => void
  onChanged: () => void
  schoolId?: string
}) {
  const [draft, setDraft] = useState({
    ...template,
    fields: withRequiredFields(template.fields),
  })
  const failed = (error: Error) => toast(error.message, 'error')

  const save = useMutation({
    mutationFn: () =>
      api.updateTemplate(template.id, {
        name: draft.name,
        layout: draft.layout,
        paper_layout: draft.paper_layout,
        fields: draft.fields,
        fields_per_row: draft.fields_per_row as 2 | 3 | 4,
        fixed_lines: draft.fixed_lines,
        margins_mm: draft.margins_mm,
        font_name: draft.font_name,
        font_size_pt: draft.font_size_pt,
        question_number_style: draft.question_number_style,
        blank_line_style: draft.blank_line_style,
        show_page_numbers: draft.show_page_numbers,
        footer_text: draft.footer_text,
        page_border: draft.page_border,
        logo_width_percent: draft.logo_width_percent,
        title_alignment: draft.title_alignment,
        details_border: draft.details_border,
        header_scope: draft.header_scope,
      }, schoolId),
    onSuccess: () => {
      onChanged()
      toast('Header saved.')
    },
    onError: failed,
  })

  const makeDefault = useMutation({
    mutationFn: () => api.setDefaultTemplate(template.id, schoolId),
    onSuccess: onChanged,
    onError: failed,
  })
  const remove = useMutation({
    mutationFn: () => api.deleteTemplate(template.id, schoolId),
    onSuccess: onChanged,
    onError: failed,
  })
  const uploadLogo = useMutation({
    mutationFn: (file: File) => api.uploadTemplateLogo(template.id, file, schoolId),
    onSuccess: onChanged,
    onError: failed,
  })

  const orderedFields = [...draft.fields].sort(
    (left, right) => Number(left.order ?? 0) - Number(right.order ?? 0),
  )
  const configured = new Map(orderedFields.map((field) => [String(field.key), field]))
  const fieldRows = [
    ...orderedFields.map((entry) => {
      const key = String(entry.key)
      const fallback = FIELDS.find(([fieldKey]) => fieldKey === key)?.[1] ?? key
      return { key, fallback, entry }
    }),
    ...FIELDS.filter(([key]) => !configured.has(key)).map(([key, fallback]) => ({
      key,
      fallback,
      entry: undefined,
    })),
  ]

  const reindex = (fields: Template['fields']) =>
    fields.map((field, order) => ({ ...field, order }))

  const toggleField = (key: string, label: string, on: boolean) => {
    const next = on
      ? [...orderedFields, { key, label, order: orderedFields.length }]
      : orderedFields.filter((field) => String(field.key) !== key)
    setDraft({ ...draft, fields: reindex(next) })
  }

  const moveField = (key: string, direction: -1 | 1) => {
    const from = orderedFields.findIndex((field) => String(field.key) === key)
    const to = from + direction
    if (from < 0 || to < 0 || to >= orderedFields.length) return
    const next = [...orderedFields]
    ;[next[from], next[to]] = [next[to], next[from]]
    setDraft({ ...draft, fields: reindex(next) })
  }

  return (
    <div className={`tc-template ${open ? 'is-open' : ''}`}>
      <button type="button" className="tc-template-row" aria-expanded={open} onClick={onToggle}>
        <span className="tc-template-title">
          <span className="tc-template-name">{template.name}</span>
          <span className="tc-template-description">
            {layoutLabel(template.layout)} ·{' '}
            {template.paper_layout === 'exam_grid' ? 'Exam grid' : 'Standard paper'} ·{' '}
            {template.header_scope === 'all_pages' ? 'Every page' : 'First page'}
          </span>
        </span>
        <span className="tc-template-row-meta">
          <Pill tone="secondary">{sheetTypeLabel(template.sheet_type)}</Pill>
          {template.is_default && <Pill tone="success">Default</Pill>}
          <span className="tc-template-field-count">{template.fields.length} fields</span>
          <span className="tc-template-edit">{open ? 'Close' : 'Edit'}</span>
        </span>
      </button>

      {open && (
        <div className="tc-template-body">
          <div className="tc-template-form">
            <div className="tc-two-up">
              <Field label="Name">
                <Input
                  value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              </Field>
              <Field label="Layout">
                <Select
                  value={draft.layout}
                  onChange={(event) => setDraft({ ...draft, layout: event.target.value })}
                >
                  {LAYOUTS.map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Paper layout"
                hint="Exam grid adds fixed Question No., work area and Marks columns."
              >
                <Select
                  value={draft.paper_layout}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      paper_layout: event.target.value as Template['paper_layout'],
                    })
                  }
                >
                  {PAPER_LAYOUTS.map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </Select>
              </Field>
            </div>
            <div className="tc-two-up">
              <Field label="Paper font" hint="Used for every question and answer choice.">
                <Select
                  value={draft.font_name}
                  onChange={(event) => setDraft({ ...draft, font_name: event.target.value })}
                >
                  {PAPER_FONTS.map((font) => (
                    <option key={font} value={font}>{font}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Font size" hint="9–18 pt; the default is 12 pt.">
                <Input
                  type="number"
                  min={9}
                  max={18}
                  step={0.5}
                  value={String(draft.font_size_pt)}
                  onChange={(event) =>
                    setDraft({ ...draft, font_size_pt: Number(event.target.value) })
                  }
                />
              </Field>
            </div>
            <div className="tc-two-up">
              <Field
                label="Main question numbers"
                hint="Choose 1, 2, 3 or I, II, III for top-level questions."
              >
                <Select
                  value={draft.question_number_style ?? 'numeric'}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      question_number_style: event.target.value as Template['question_number_style'],
                    })
                  }
                >
                  <option value="numeric">Numeric — 1, 2, 3, 4</option>
                  <option value="roman">Roman — I, II, III, IV</option>
                </Select>
              </Field>
              <Field
                label="Blank writing lines"
                hint="Use dotted lines like traditional exam papers, or solid lines."
              >
                <Select
                  value={draft.blank_line_style ?? 'dotted'}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      blank_line_style: event.target.value as Template['blank_line_style'],
                    })
                  }
                >
                  <option value="dotted">Dotted</option>
                  <option value="solid">Solid</option>
                </Select>
              </Field>
            </div>

            <div className="tc-header-identity-controls">
              <Field
                label="Logo width"
                hint="The logo always uses at least 30% of the printable header width."
              >
                <div className="tc-logo-width-control">
                  <input
                    type="range"
                    min={30}
                    max={50}
                    step={5}
                    value={draft.logo_width_percent}
                    aria-label="Logo width percentage"
                    onChange={(event) =>
                      setDraft({ ...draft, logo_width_percent: Number(event.target.value) })
                    }
                  />
                  <output>{draft.logo_width_percent}%</output>
                </div>
              </Field>
              <Field label="Title alignment" hint="Position the paper title beside or below the logo.">
                <Select
                  value={draft.title_alignment}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      title_alignment: event.target.value as Template['title_alignment'],
                    })
                  }
                >
                  <option value="left">Left</option>
                  <option value="center">Centre</option>
                  <option value="right">Right</option>
                </Select>
              </Field>
            </div>

            <Field
              label="Details per row"
              hint="Choose how many header fields appear across each printed row."
            >
              <Select
                value={String(draft.fields_per_row)}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    fields_per_row: Number(event.target.value) as 2 | 3 | 4,
                  })
                }
              >
                <option value="2">2 per row</option>
                <option value="3">3 per row</option>
                <option value="4">4 per row</option>
              </Select>
            </Field>

            <div className="tc-header-details-border">
              <Checkbox
                label="Draw a border around Class, Section, Subject, Name and other header details"
                checked={draft.details_border}
                onChange={(event) =>
                  setDraft({ ...draft, details_border: event.target.checked })
                }
              />
            </div>

            <fieldset className="tc-header-fields-editor">
              <legend className="tc-field-label">Header parameters</legend>
              <p className="tc-field-hint">
                Useful details are selected by default. Switch off any you do not need;
                Class and Name stay enabled.
              </p>
              <div className="tc-header-fields-head" aria-hidden="true">
                <span>Show</span>
                <span>Parameter</span>
                <span>Printed name</span>
                <span>Order</span>
              </div>
              <div className="tc-header-fields-list">
                {fieldRows.map(({ key, fallback: label, entry }, rowIndex) => {
                const required = REQUIRED_FIELDS.has(key)
                const enabledIndex = orderedFields.findIndex(
                  (field) => String(field.key) === key,
                )
                return (
                  <div
                    key={key}
                    className={`tc-header-field-row ${entry ? '' : 'is-disabled'}`}
                  >
                    <div className="tc-header-field-enabled">
                      <Checkbox
                        label={<span className="tc-sr-only">Show {label}</span>}
                        checked={Boolean(entry)}
                        disabled={required}
                        onChange={(event) =>
                          toggleField(key, label, event.target.checked)
                        }
                      />
                    </div>
                    <div className="tc-header-field-parameter">
                      <strong>{label}</strong>
                      {required && (
                        <span className="tc-required-badge">
                          Required<span className="tc-sr-only"> parameter</span>
                        </span>
                      )}
                    </div>
                    <div className="tc-header-field-label">
                      <Input
                        aria-label={`Printed name for ${label}`}
                        value={String(entry?.label ?? label)}
                        disabled={!entry}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            fields: draft.fields.map((field) =>
                              String(field.key) === key
                                ? { ...field, label: event.target.value }
                                : field,
                            ),
                          })
                        }
                      />
                    </div>
                    <div className="tc-header-field-order">
                      <Button
                        size="sm"
                        className="tc-field-order-button"
                        aria-label={`Move ${label} up`}
                        title={`Move ${label} up`}
                        disabled={!entry || enabledIndex <= 0}
                        onClick={() => moveField(key, -1)}
                      >
                        <Icon.up size={15} />
                      </Button>
                      <Button
                        size="sm"
                        className="tc-field-order-button"
                        aria-label={`Move ${label} down`}
                        title={`Move ${label} down`}
                        disabled={
                          !entry ||
                          enabledIndex < 0 ||
                          enabledIndex >= orderedFields.length - 1
                        }
                        onClick={() => moveField(key, 1)}
                      >
                        <Icon.down size={15} />
                      </Button>
                      <span className="tc-header-field-position" aria-label={`Position ${rowIndex + 1}`}>
                        {entry ? enabledIndex + 1 : '—'}
                      </span>
                    </div>
                  </div>
                )
                })}
              </div>
            </fieldset>

            <Field label="Show this header on">
              <Select
                value={draft.header_scope}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    header_scope: event.target.value as Template['header_scope'],
                  })
                }
              >
                <option value="first_page">First page only</option>
                <option value="all_pages">Every page</option>
              </Select>
            </Field>

            <details className="tc-details">
              <summary>Advanced</summary>
              <div className="tc-details-body">
                <Field
                  label="Fixed lines"
                  hint="Optional lines printed under the title, one per line."
                >
                  <Textarea
                    rows={2}
                    placeholder=""
                    value={draft.fixed_lines.join('\n')}
                    onChange={(event) =>
                      setDraft({ ...draft, fixed_lines: event.target.value.split('\n') })
                    }
                  />
                </Field>
                <Field
                  label="Paper border"
                  hint="Print a border around the edge of every page."
                >
                  <Select
                    value={draft.page_border}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        page_border: event.target.value as Template['page_border'],
                      })
                    }
                  >
                    <option value="none">No border</option>
                    <option value="thin">Thin border</option>
                    <option value="thick">Thick border</option>
                  </Select>
                </Field>
                <div className="tc-four-up">
                  {['Top', 'Bottom', 'Left', 'Right'].map((side, index) => (
                    <Field key={side} label={`${side} mm`}>
                      <Input
                        type="number"
                        min={8}
                        max={40}
                        value={String(draft.margins_mm[index] ?? 20)}
                        onChange={(event) => {
                          const margins = [...draft.margins_mm]
                          margins[index] = Number(event.target.value)
                          setDraft({ ...draft, margins_mm: margins })
                        }}
                      />
                    </Field>
                  ))}
                </div>
                <Checkbox
                  label="Print page numbers"
                  checked={draft.show_page_numbers}
                  onChange={(event) =>
                    setDraft({ ...draft, show_page_numbers: event.target.checked })
                  }
                />
                <Field label="Footer text">
                  <Input
                    value={draft.footer_text}
                    onChange={(event) => setDraft({ ...draft, footer_text: event.target.value })}
                  />
                </Field>
              </div>
            </details>

            <div className="tc-template-actions">
              <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
                Save header
              </Button>

              <label className="tc-btn tc-btn-secondary tc-btn-md" style={{ cursor: 'pointer' }}>
                {template.logo_image_id ? 'Change school logo' : 'Upload school logo'}
                <input
                  type="file"
                  accept="image/*"
                  className="tc-sr-only"
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (file) uploadLogo.mutate(file)
                    event.target.value = ''
                  }}
                />
              </label>

              {!template.is_default && (
                <Button onClick={() => makeDefault.mutate()}>
                  Use for all {sheetTypeLabel(template.sheet_type)} papers
                </Button>
              )}

              <div className="tc-spacer" />
              <ConfirmButton
                label="Delete header"
                message="If this is the school default, matching papers will move to the next header for this paper type."
                onConfirm={() => remove.mutate()}
              />
            </div>
          </div>

          <HeaderPagePreview
            templateId={template.id}
            template={draft}
            logoImageId={template.logo_image_id}
            schoolId={schoolId}
          />
        </div>
      )}
    </div>
  )
}

function HeaderPagePreview({
  templateId,
  template,
  logoImageId,
  schoolId,
}: {
  templateId: string
  template: Template
  logoImageId?: string | null
  schoolId?: string
}) {
  const [downloading, setDownloading] = useState<'docx' | 'pdf' | null>(null)
  const fields = [...template.fields].sort(
    (left, right) => Number(left.order ?? 0) - Number(right.order ?? 0),
  )
  const fixedLines = template.fixed_lines.map((line) => line.trim()).filter(Boolean)
  const layout = LAYOUTS.some(([key]) => key === template.layout)
    ? template.layout
    : 'centered'
  const scopeLabel =
    template.header_scope === 'all_pages' ? 'Repeated on every page' : 'Shown on page 1 only'
  const [top, bottom, left, right] = [0, 1, 2, 3].map((index) =>
    clampMargin(Number(template.margins_mm[index] ?? 20)),
  )
  const previewData: PreviewData = {
    page_count: 1,
    content_width_mm: 210 - left - right,
    question_spacing: 'compact',
    total_marks: null,
    is_estimate: true,
    header: {
      school_name: '',
      school_branch: '',
      title: 'Paper title',
      title_size: 'standard',
      layout,
      paper_layout: template.paper_layout,
      scope: template.header_scope,
      logo_image_id: logoImageId ?? null,
      logo_width_percent: template.logo_width_percent,
      title_alignment: template.title_alignment,
      details_border: template.details_border,
      fixed_lines: fixedLines,
      fields: fields.map((field) => ({
        key: String(field.key),
        label: String(field.label || field.key),
        value: null,
      })),
      fields_per_row: template.fields_per_row,
      margin_top_mm: top,
      margin_bottom_mm: bottom,
      margin_left_mm: left,
      margin_right_mm: right,
      show_page_numbers: template.show_page_numbers,
      footer_text: template.footer_text || null,
      page_border: template.page_border,
      font_name: template.font_name,
      font_size_pt: template.font_size_pt,
      question_number_style: template.question_number_style,
      blank_line_style: template.blank_line_style,
    },
    pages: [
      {
        number: 1,
        items: [{ kind: 'header' }],
        question_ids: [],
        used_mm: 0,
        capacity_mm: 297 - top - bottom,
      },
    ],
  }

  const downloadPreview = async (format: 'docx' | 'pdf') => {
    setDownloading(format)
    try {
      const blob = await api.templatePreview(
        templateId,
        format,
        {
          layout: template.layout as 'centered' | 'logo_left' | 'logo_right' | 'banner',
          paper_layout: template.paper_layout,
          fields: template.fields,
          fields_per_row: template.fields_per_row as 2 | 3 | 4,
          fixed_lines: template.fixed_lines,
          margins_mm: template.margins_mm,
          show_page_numbers: template.show_page_numbers,
          footer_text: template.footer_text,
          page_border: template.page_border,
          logo_width_percent: template.logo_width_percent,
          title_alignment: template.title_alignment,
          details_border: template.details_border,
          header_scope: template.header_scope,
          font_name: template.font_name,
          font_size_pt: template.font_size_pt,
          question_number_style: template.question_number_style,
          blank_line_style: template.blank_line_style,
        },
        schoolId,
      )
      const basename =
        template.name.trim().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '_') || 'paper_header'
      saveBlob(blob, `${basename}_header_preview.${format}`)
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not download the preview.', 'error')
    } finally {
      setDownloading(null)
    }
  }

  return (
    <aside className="tc-template-preview" aria-label={`Live preview of ${template.name}`}>
      <div className="tc-template-preview-head">
        <div className="tc-template-preview-title">
          <strong>Page preview</strong>
          <span>Download this header</span>
        </div>
        <div className="tc-template-preview-actions">
          <div className="tc-template-download-group" role="group" aria-label="Download preview">
            <Button
              className="tc-template-download-button"
              size="sm"
              icon={<Icon.download size={14} />}
              disabled={downloading !== null}
              onClick={() => downloadPreview('docx')}
            >
              {downloading === 'docx' ? 'Preparing…' : 'Word'}
            </Button>
            <Button
              className="tc-template-download-button"
              size="sm"
              icon={<Icon.download size={14} />}
              disabled={downloading !== null}
              onClick={() => downloadPreview('pdf')}
            >
              {downloading === 'pdf' ? 'Preparing…' : 'PDF'}
            </Button>
          </div>
          <Pill tone="secondary">Live</Pill>
        </div>
      </div>

      <div className="tc-template-shared-preview">
        <PaperPreview data={previewData} />
      </div>

      <div className="tc-template-preview-note">
        <span>{scopeLabel}</span>
        <span>Teachers replace “Paper title” and may choose its bold size.</span>
        {!logoImageId && <span>Upload a logo to include it in the header.</span>}
      </div>
    </aside>
  )
}

function clampMargin(value: number) {
  return Number.isFinite(value) ? Math.min(40, Math.max(8, value)) : 20
}

function withRequiredFields(fields: Template['fields']): Template['fields'] {
  const ordered = [...fields].sort(
    (left, right) => Number(left.order ?? 0) - Number(right.order ?? 0),
  )
  const configured = new Set(ordered.map((field) => String(field.key)))
  const missing = FIELDS.filter(
    ([key]) => REQUIRED_FIELDS.has(key) && !configured.has(key),
  ).map(([key, label]) => ({ key, label, order: ordered.length }))
  return [...ordered, ...missing].map((field, order) => ({ ...field, order }))
}

function layoutLabel(layout: string) {
  return LAYOUTS.find(([key]) => key === layout)?.[1] ?? 'Custom layout'
}
